import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "sonner";

import Login from "./pages/Login";
import Register from "./pages/Register";
import ResetPassword from "./pages/ResetPassword";
import AcceptInvite from "./pages/AcceptInvite";
import SellerPortal from "./pages/SellerPortal";
import LandingPage from "./pages/LandingPage";
import Layout from "./components/Layout";
import Dashboard from "./pages/Dashboard";
import Pipeline from "./pages/Pipeline";
import DealDetail from "./pages/DealDetail";
import Contacts from "./pages/Contacts";
import BuyerProfile from "./pages/BuyerProfile";
import Tasks from "./pages/Tasks";
import Valuation from "./pages/Valuation";
import AIHub from "./pages/AIHub";
import Opportunities from "./pages/Opportunities";
import Settings from "./pages/Settings";
import Outcomes from "./pages/Outcomes";
import Compliance from "./pages/Compliance";
import Forecast from "./pages/Forecast";
import SuperAdmin from "./pages/SuperAdmin";
import Billing from "./pages/Billing";
import Leakage from "./pages/Leakage";
import ManagingPartner from "./pages/ManagingPartner";
import Origination from "./pages/Origination";
import Sourcing from "./pages/Sourcing";
import SourcingPipeline from "./pages/SourcingPipeline";
import SectorPacks from "./pages/SectorPacks";
import TargetAnalysis from "./pages/TargetAnalysis";
import TupeCalculator from "./pages/TupeCalculator";
import DistressWatchlist from "./pages/DistressWatchlist";
import DistressedMandates from "./pages/DistressedMandates";
import DistressedMandateDetail from "./pages/DistressedMandateDetail";
import CashFlowBuilder from "./pages/CashFlowBuilder";
import StabilisationTracker from "./pages/StabilisationTracker";
import StructureComparison from "./pages/StructureComparison";

function Protected({ children }) {
    const { user, ready } = useAuth();
    if (!ready) return <div className="h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;
    if (!user) return <Navigate to="/login" replace />;
    return children;
}

function HomeRouter() {
    const { user } = useAuth();
    if (user?.role === "super_admin") return <Navigate to="/super-admin" replace />;
    return <Dashboard />;
}

function PublicOnly({ children }) {
    const { user, ready } = useAuth();
    if (!ready) return null;
    if (user) return <Navigate to="/dashboard" replace />;
    return children;
}

function RootRouter() {
    const { user, ready } = useAuth();
    if (!ready) return <div className="h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;
    if (user) {
        if (user.role === "super_admin") return <Navigate to="/super-admin" replace />;
        return <Navigate to="/dashboard" replace />;
    }
    return <LandingPage />;
}

function App() {
    useEffect(() => {
        document.title = "Closvio";
    }, []);

    return (
        <div className="App">
            <AuthProvider>
                <Toaster position="top-right" richColors />
                <BrowserRouter>
                    <Routes>
                        <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
                        <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/accept-invite" element={<AcceptInvite />} />
                        <Route path="/portal/:token" element={<SellerPortal />} />
                        <Route path="/opportunities" element={<Opportunities />} />

                        <Route path="/" element={<RootRouter />} />

                        <Route element={<Protected><Layout /></Protected>}>
                            <Route path="/dashboard" element={<HomeRouter />} />
                            <Route path="/pipeline" element={<Pipeline />} />
                            <Route path="/deals/:id" element={<DealDetail />} />
                            <Route path="/contacts" element={<Contacts />} />
                            <Route path="/contacts/:id" element={<BuyerProfile />} />
                            <Route path="/tasks" element={<Tasks />} />
                            <Route path="/valuation" element={<Valuation />} />
                            <Route path="/ai" element={<AIHub />} />
                            <Route path="/outcomes" element={<Outcomes />} />
                            <Route path="/forecast" element={<Forecast />} />
                            <Route path="/compliance" element={<Compliance />} />
                            <Route path="/billing" element={<Billing />} />
                            <Route path="/intelligence/leakage" element={<Leakage />} />
                            <Route path="/managing-partner" element={<ManagingPartner />} />
                            <Route path="/intelligence/origination" element={<Origination />} />
                            <Route path="/sourcing" element={<Navigate to="/sourcing/mandates" replace />} />
                            <Route path="/sourcing/mandates" element={<Sourcing />} />
                            <Route path="/sourcing/pipeline" element={<SourcingPipeline />} />
                            <Route path="/sourcing/sectors" element={<SectorPacks />} />
                            <Route path="/sourcing/company/:number" element={<TargetAnalysis />} />
                            <Route path="/distressed/watchlist" element={<DistressWatchlist />} />
                            <Route path="/distressed/tupe-calculator" element={<TupeCalculator />} />
                            <Route path="/distressed/mandates" element={<DistressedMandates />} />
                            <Route path="/distressed/mandates/:id" element={<DistressedMandateDetail />} />
                            <Route path="/distressed/cashflow" element={<CashFlowBuilder />} />
                            <Route path="/distressed/cashflow/:id" element={<CashFlowBuilder />} />
                            <Route path="/distressed/stabilisation" element={<StabilisationTracker />} />
                            <Route path="/distressed/structuring" element={<StructureComparison />} />
                            <Route path="/distressed/structuring/:id" element={<StructureComparison />} />
                            <Route path="/super-admin" element={<SuperAdmin />} />
                            <Route path="/settings" element={<Settings />} />
                        </Route>

                        <Route path="*" element={<Navigate to="/" />} />
                    </Routes>
                </BrowserRouter>
            </AuthProvider>
        </div>
    );
}

export default App;
